import { type ReactNode, useEffect, useRef, useState } from 'react'
import { Box } from '@dhl-ui/components'
import { useHeaderLayout } from '../context/HeaderLayout'

type Props = {
  /**
   * Unique reference for the item
   */
  id: string

  /**
   * You can not have mutliple items with same priority. The lowest priority will
   * be compacted last when there is no room for displaying the non compact version
   */
  priority: number

  /**
   * Our non compacted version of our header item
   */
  children: ReactNode

  /**
   * Our compacted version of our header item
   */
  compactChildren: ReactNode
}

/**
 * Each HeaderItem needs to have a unique priority, you can not have them more then once
 * This Item wrapper will make sure your item gets painted correctly when the available room
 * allows it to be based on its priority. Highest priority gets compacted last.
 */
export const HeaderItem = ({ id, priority, children, compactChildren }: Props) => {
  const { registerItem, compactIndex } = useHeaderLayout()
  const [width, setWidth] = useState(0)
  const [widthCompact, setWidthCompact] = useState(0)
  const ref = useRef<HTMLDivElement>(null)
  const [opacity, setOpacity] = useState(0)

  const isCompact = priority >= compactIndex

  useEffect(() => {
    const timer = setTimeout(() => setOpacity(1), 50) // Slight delay for smoothness
    return () => clearTimeout(timer)
  }, [])

  /**
   * Look for DOM resize
   */
  useEffect(() => {
    if (!ref.current || typeof ResizeObserver === 'undefined') {
      return undefined
    }

    const observer = new ResizeObserver(([entry]) => {
      const newWidth = entry?.contentRect.width

      if (!newWidth) {
        return
      }

      /**
       * We have to set the correct width and widthCompact in order
       * for our layout manager to see if the item fits or needs to be adjusted
       * our default width starts with 0, in the first iteration every component
       * will be painted non compacted so this will set our width
       *
       * When we do see its compacted we see what our new width has become and
       * update accordingly. Now the layout manager will have both values to do
       * its calculations
       */
      if (isCompact && widthCompact !== newWidth) {
        setWidthCompact(newWidth)
        registerItem({ id, priority, width, widthCompact: newWidth })
      }

      if (!isCompact && width !== newWidth) {
        setWidth(newWidth)
        registerItem({ id, priority, width: newWidth, widthCompact })
      }
    })

    observer.observe(ref.current)

    return () => {
      observer.disconnect()
    }
  }, [id, priority, width, widthCompact, isCompact, registerItem])

  return (
    <Box ref={ref} sx={{ opacity, transition: 'opacity 220ms ease-in-out' }}>
      {isCompact && compactChildren ? compactChildren : children}
    </Box>
  )
}
